import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldAlert, Clock } from "lucide-react";
import { APP_ROLE_LABELS, type AppRole } from "@/lib/permissions";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Allowed roles. If omitted, falls back to requireAdmin behavior. */
  allowedRoles?: AppRole[];
  /** Legacy prop — maps to allowedRoles: ['admin'] */
  requireAdmin?: boolean;
}

const STUCK_TIMEOUT = 15_000; // 15 seconds
const AUTH_TIMEOUT = 30_000; // 30 seconds for initial auth check

export const ProtectedRoute = ({ children, allowedRoles, requireAdmin }: ProtectedRouteProps) => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isStuck, setIsStuck] = useState(false);

  // Resolve effective allowed roles
  const effectiveRoles: AppRole[] | null = allowedRoles ?? (requireAdmin !== false ? ['admin'] : null);
  const needsRoleCheck = effectiveRoles !== null;

  // FIX: Only fetch role AFTER authentication is confirmed to prevent
  // caching a null role when the session hasn't been restored yet
  const { data: userRole, isLoading: roleLoading, isError: roleError } = useUserRole({
    enabled: isAuthenticated === true && needsRoleCheck,
  });

  useEffect(() => {
    console.log('[ProtectedRoute] Mounting — checking auth state...');

    // Set up listener FIRST (Supabase best practice) — catches token refreshes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`[ProtectedRoute] Auth state change: ${event}, session: ${!!session}`);
      
      // Clear stale navigation state on fresh login so user starts at 1/N
      if (event === 'SIGNED_IN') {
        sessionStorage.removeItem('dashboard-last-booking-id');
        sessionStorage.removeItem('messages-last-booking-id');
        sessionStorage.removeItem('stay-agent-current-index');
        console.log('[ProtectedRoute] Cleared session navigation keys on SIGNED_IN');
      }
      
      setIsAuthenticated(!!session);
    });

    // Then check current session with a timeout to prevent hangs
    let authTimedOut = false;
    const checkAuth = async () => {
      try {
        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise<null>((resolve) => setTimeout(() => {
            authTimedOut = true;
            resolve(null);
          }, AUTH_TIMEOUT)),
        ]);

        if (authTimedOut || !sessionResult) {
          console.warn('[ProtectedRoute] Auth session check timed out — treating as unauthenticated');
          setIsAuthenticated(false);
          return;
        }

        const { data: { session } } = sessionResult as Awaited<ReturnType<typeof supabase.auth.getSession>>;
        console.log(`[ProtectedRoute] Session check result: ${!!session}${session ? ` (user: ${session.user.id.slice(0, 8)}...)` : ''}`);
        setIsAuthenticated(!!session);
      } catch (err) {
        console.error('[ProtectedRoute] Auth check error:', err);
        setIsAuthenticated(false);
      }
    };

    checkAuth();

    return () => subscription.unsubscribe();
  }, []);

  // Detect stuck loading state
  const isCurrentlyLoading = isAuthenticated === null || (needsRoleCheck && isAuthenticated && roleLoading && !roleError);

  useEffect(() => {
    if (!isCurrentlyLoading) {
      setIsStuck(false);
      return;
    }
    const timer = setTimeout(() => {
      console.warn(`[ProtectedRoute] Stuck for ${STUCK_TIMEOUT / 1000}s — auth: ${isAuthenticated}, roleLoading: ${roleLoading}, roleError: ${roleError}, role: ${userRole}`);
      setIsStuck(true);
    }, STUCK_TIMEOUT);
    return () => clearTimeout(timer);
  }, [isCurrentlyLoading]);

  // Log decision points
  useEffect(() => {
    if (isAuthenticated !== null) {
      console.log(`[ProtectedRoute] Decision state — auth: ${isAuthenticated}, role: ${userRole ?? 'null'}, roleLoading: ${roleLoading}, roleError: ${roleError}, needsRoleCheck: ${needsRoleCheck}, effectiveRoles: ${effectiveRoles?.join(',') ?? 'any'}`);
    }
  }, [isAuthenticated, userRole, roleLoading, roleError]);

  // Loading state
  if (isCurrentlyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="space-y-4 w-full max-w-md text-center">
          {isStuck ? (
            <>
              <p className="text-muted-foreground">{t('auth.stuckMessage')}</p>
              <Button onClick={async () => {
                await supabase.auth.signOut().catch(() => {});
                window.location.href = '/auth';
              }} className="w-full">
                {t('auth.signInAgain')}
              </Button>
              <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
                {t('common.reload')}
              </Button>
            </>
          ) : (
            <>
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </>
          )}
        </div>
      </div>
    );
  }

  // Not logged in
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated — redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Role check failed after all retries — offer recovery
  if (needsRoleCheck && roleError) {
    console.error('[ProtectedRoute] Role check failed after retries — showing error recovery');
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-border">
          <CardHeader className="text-center">
            <CardTitle className="font-playfair text-2xl">{t('auth.connectionError')}</CardTitle>
            <CardDescription>
              {t('auth.permissionCheckFailed')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={() => window.location.reload()} className="w-full">
              {t('common.retry')}
            </Button>
            <Button onClick={async () => {
              await supabase.auth.signOut().catch(() => {});
              window.location.href = '/auth';
            }} variant="outline" className="w-full">
              {t('auth.signInAgain')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authenticated but no role assigned — pending approval
  if (needsRoleCheck && !userRole) {
    console.warn(`[ProtectedRoute] User authenticated but no role found — showing pending screen`);
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-border">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-amber-500/10 rounded-full">
                <Clock className="h-8 w-8 text-amber-500" />
              </div>
            </div>
            <CardTitle className="font-playfair text-2xl">{t('auth.accountPending')}</CardTitle>
            <CardDescription>
              {t('auth.accountNotActivated')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-6">
              {t('auth.adminMustAssignRole')}
            </p>
            <Button onClick={async () => { await supabase.auth.signOut(); window.location.href = '/auth'; }} variant="outline" className="w-full">
              {t('auth.signOut')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Has role but not in allowed list
  if (needsRoleCheck && effectiveRoles && userRole && !effectiveRoles.includes(userRole)) {
    const roleLabel = APP_ROLE_LABELS[userRole] || userRole;
    console.warn(`[ProtectedRoute] Role '${userRole}' not in allowed list [${effectiveRoles.join(',')}]`);
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-border">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-destructive/10 rounded-full">
                <ShieldAlert className="h-8 w-8 text-destructive" />
              </div>
            </div>
            <CardTitle className="font-playfair text-2xl">{t('auth.accessDenied')}</CardTitle>
            <CardDescription>
              {t('auth.insufficientPermissions')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-6">
              {t('auth.currentRoleInsufficient', { role: roleLabel })}
            </p>
            <Button onClick={() => window.location.href = '/'} className="w-full">
              {t('common.backToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
